import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { Dialog, Popover } from 'radix-ui';
import { KitButton } from './button';

export function useSheetPlacement() {
  const [sheet, setSheet] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 640px), (pointer: coarse)');
    const update = () => setSheet(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return sheet;
}
export interface AdaptiveOverlayProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  disabled?: boolean;
  title: string;
  closeLabel: string;
  trigger: ReactElement;
  children: ReactNode;
  actions?: ReactNode;
  anchorOnly?: boolean;
  keepAnchorFocus?: boolean;
}
export function AdaptiveOverlay({
  open,
  onOpenChange,
  disabled = false,
  title,
  closeLabel,
  trigger,
  children,
  actions,
  anchorOnly = false,
  keepAnchorFocus = false,
}: AdaptiveOverlayProps) {
  const sheet = useSheetPlacement();
  const [host, setHost] = useState<HTMLElement | null>(null);
  const anchor = useCallback((node: HTMLSpanElement | null) => {
    setHost(node?.closest<HTMLElement>('.pc-portal') ?? null);
  }, []);
  const visible = open && !disabled;
  const composingEscape = (event: KeyboardEvent) => {
    // Radix observes Escape on document capture, before React input handlers.
    if (event.isComposing || event.keyCode === 229) event.preventDefault();
  };
  const body = (
    <>
      <div className="pc-sheet-body">{children}</div>
      {actions && <div className="pc-sheet-actions">{actions}</div>}
    </>
  );
  if (sheet)
    return (
      <Dialog.Root open={visible} onOpenChange={onOpenChange}>
        <span ref={anchor}>
          {anchorOnly ? trigger : <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
        </span>
        <Dialog.Portal container={host ?? undefined}>
          <Dialog.Overlay className="pc-sheet-scrim" data-state={visible ? 'open' : 'closing'} />
          <Dialog.Content
            className="pc-sheet"
            data-placement="sheet"
            data-state={visible ? 'open' : 'closing'}
            aria-describedby={undefined}
            onEscapeKeyDown={composingEscape}
          >
            <div className="pc-sheet-handle" aria-hidden="true" />
            <Dialog.Title className="pc-sheet-title">{title}</Dialog.Title>
            {body}
            <Dialog.Close asChild>
              <KitButton variant="quiet">{closeLabel}</KitButton>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  return (
    <Popover.Root open={visible} onOpenChange={onOpenChange}>
      <span ref={anchor}>
        {anchorOnly ? (
          <Popover.Anchor asChild>{trigger}</Popover.Anchor>
        ) : (
          <Popover.Trigger asChild>{trigger}</Popover.Trigger>
        )}
      </span>
      <Popover.Portal container={host ?? undefined}>
        <Popover.Content
          className="pc-sheet"
          collisionPadding={16}
          side="top"
          align="start"
          data-placement="popover"
          data-state={visible ? 'open' : 'closing'}
          aria-label={title}
          onEscapeKeyDown={composingEscape}
          onOpenAutoFocus={(event) => {
            if (keepAnchorFocus) event.preventDefault();
          }}
        >
          <h2 className="pc-sheet-title">{title}</h2>
          {body}
          <Popover.Close asChild>
            <KitButton variant="quiet">{closeLabel}</KitButton>
          </Popover.Close>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
